import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { MRT_Row, MRT_ColumnDef } from "material-react-table"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import {
    getEditServiceCenterAdminRoute,
    SERVICE_CENTER_ADMIN_ACCOUNT_ADMINISTRATORS_CREATE_ROUTE,
    SERVICE_CENTER_ADMIN_ROUTE,
} from "../../../../../../constants/routes"
import React, { useCallback, useMemo, useState } from "react"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { MenuItem } from "@mui/material"
import { useNavigate } from "react-router"
import { UserDetailsDialog } from "../../../../../office/admin/components/UserDetails/UserDetailsDialog"
import { fetchServiceCenterAdmins, ServiceCenterAdminsQuery, ServiceCenterAdminsResponse } from "../adminsApi"
import { ServiceCenterAdminsItem } from "../adminsTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveServiceCenterId } from "../../../../../context/contextSelectors"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { deleteAdmin } from "../../../../../users/userApi"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { DestructiveMenuItem } from "../../../../../../shared/components/menus/DestructiveMenuItem"

type Props = Readonly<{}>

function transformData(data: ServiceCenterAdminsResponse) {
    return data.administrators
}

export function ServiceCenterAccountAdmins({}: Props) {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)

    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [admin, setAdmin] = useState<ServiceCenterAdminsItem | null>()

    const serviceCenterAdmins = useApiQuery(fetchServiceCenterAdmins, {
        queryName: ServiceCenterAdminsQuery.ADMINS_FOR_SERVICE_CENTER,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
        },
        enabled: Boolean(serviceCenterId),
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(serviceCenterAdmins)

    const { mutate: callDeleteAdmin } = useApiMutation(deleteAdmin, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterAdminsQuery.ADMINS_FOR_SERVICE_CENTER],
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (!admin) {
            return
        }

        callDeleteAdmin({ userId: admin.user.id })
    }, [admin, callDeleteAdmin])

    const columns = useMemo<MRT_ColumnDef<ServiceCenterAdminsItem>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("service-center-admin-account-admins-table-header-name"),
                accessorKey: "user.displayName",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("service-center-admin-account-admins-table-header-email"),
                accessorKey: "user.email",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("service-center-admin-account-admins-table-header-status"),
                    accessorKey: "user.isActive",
                },
                (row) => row?.user.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<ServiceCenterAdminsItem>; closeMenu: () => void }) => [
            <MenuItem
                key={`details-${row.original.user.id}`}
                onClick={() => {
                    setAdmin(row.original)
                    setShowDetailsModal(true)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="service-center-admin-account-admins-table-button-details">
                    <span>Show details</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`edit-${row.original.user.id}`}
                onClick={() => {
                    navigate(getEditServiceCenterAdminRoute(row.original.user.id))
                    closeMenu()
                }}
            >
                <LocalizedStrict id="service-center-admin-account-admins-table-button-edit">
                    <span>Edit user</span>
                </LocalizedStrict>
            </MenuItem>,
            <DestructiveMenuItem
                key={`delete-${row.original.user.id}`}
                onClick={() => {
                    setAdmin(row.original)
                    setShowDeleteModal(true)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="service-center-admin-account-admins-table-button-delete">
                    <span>Delete user</span>
                </LocalizedStrict>
            </DestructiveMenuItem>,
        ],
        [navigate],
    )

    return (
        <>
            <LocalizedStrict id="service-center-admin-account-admins-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SERVICE_CENTER_ADMIN_ACCOUNT_ADMINS_ROUTE}
                    title="Administrators"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="service-center-admin-account-admins-button-create">
                            <StandardButton
                                onClick={() => {
                                    navigate(SERVICE_CENTER_ADMIN_ACCOUNT_ADMINISTRATORS_CREATE_ROUTE)
                                }}
                            >
                                Create new
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-nav-item-account">
                            <Breadcrumb>Account</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-account-administrators">
                            <Breadcrumb>Administrators</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
            />

            {admin && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("service-center-admin-account-admins-delete-dialog-entity-type")}
                    entityName={admin.user.displayName}
                    handleClick={handleDeleteClick}
                    open={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                />
            )}

            {admin && (
                <UserDetailsDialog
                    key={admin.user.id}
                    userId={admin?.user.id}
                    userType="admin"
                    open={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false)
                    }}
                />
            )}
        </>
    )
}
